import moment from 'moment';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { CellProps } from 'react-table';
import { toast } from 'react-toastify';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import CobuTableAdvanced from '~/components/CobuTableAdvanced/CobuTableAdvanced';
import GenericModal from '~/components/GenericModal/GenericModal';
import Spacer from '~/components/Spacer/Spacer';
import Spinner from '~/components/Spinner/Spinner';
import { Tabs } from '~/components/Tabs';
import { Roles } from '~/enums/Roles';
import {
  getCreateTooltipFormConfig
} from '~/helpers/formHelpers';
import CobuConstants from '~/helpers/cobuConstants';
import useTooltips from '~/hooks/surveys/useTooltips';
import { RootState } from '~/redux/store';
import { Tooltip } from '~/types/tooltip';
import CreateTooltipForm from '~/components/CreateSurveyForm/CreateTooltipForm';
import styles from './ActiveSurvey.module.css';

type TabValue = 'active' | 'scheduled';

const ActiveTooltips = () => {
  const {
    activeTooltips,
    scheduledTooltips,
    loadingConfig,
    deactivateTooltip,
    createTooltip,
    updateTooltip,
    bulkUploadTooltips
  } = useTooltips();
  const { userRole } = useSelector((state: RootState) => ({
    userRole: state.auth.userRole
  }));
  const isSuperAdmin = userRole === Roles.Superadmin;
  const [showTooltipCreateModal, setShowTooltipCreateModal] =
    useState<boolean>(false);
  const [showCsvUploadModal, setShowCsvUploadModal] = useState<boolean>(false);
  const [csvFile, setCsvFile] = useState<File | undefined>(undefined);
  const [csvUploading, setCsvUploading] = useState<boolean>(false);
  const [csvScheduledActiveDate, setCsvScheduledActiveDate] = useState<string>('');
  const [csvScheduledDeactiveDate, setCsvScheduledDeactiveDate] = useState<string>('');
  const csvInputRef = useRef<HTMLInputElement>(null);
  const [tooltipToEdit, setTooltipToEdit] = useState<Tooltip | null>(null);
  const [selectedTab, setSelectedTab] = useState<TabValue>('active');

  /* UI Components */

  const activeTooltipTableConfig = [
    {
      Header: 'BUILDING',
      accessor: 'building.name',
      Cell: (cell: CellProps<Tooltip>) => (
        <div style={{ whiteSpace: 'normal' }} className='default-text'>
          {cell.row.original.building?.name}
        </div>
      )
    },
    {
      Header: 'TITLE',
      accessor: 'title',
      Cell: (cell: CellProps<Tooltip>) => {
        return (
          <div style={{ whiteSpace: 'normal' }} className='default-text'>
            {cell.row.original.title}
          </div>
        );
      }
    },
    {
      Header: 'DESCRIPTION',
      accessor: 'description',
      Cell: (cell: CellProps<Tooltip>) => {
        return (
          <div style={{ whiteSpace: 'normal' }} className='default-text'>
            {cell.row.original.description}
          </div>
        );
      }
    },
    {
      Header: 'LINK URL',
      Cell: (cell: CellProps<Tooltip>) => {
        return (
          <a
            href={cell.row.original.linkURL}
            target='_blank'
            className='default-text'
            style={{ whiteSpace: 'normal', wordBreak: 'break-all' }}
          >
            {cell.row.original.linkType === 'External'
              ? 'View External Link'
              : cell.row.original.linkURL}
          </a>
        );
      }
    },
    {
      Header: 'CALL TO ACTION',
      accessor: 'callToAction',
      Cell: (cell: CellProps<Tooltip>) => (
        <div style={{ whiteSpace: 'normal' }} className='default-text'>
          {cell.row.original.callToAction}
        </div>
      )
    },
    {
      Header: 'TYPE',
      accessor: 'linkType',
      Cell: (cell: CellProps<Tooltip>) => {
        return (
          <div style={{ whiteSpace: 'normal' }} className='default-text'>
            {cell.row.original?.linkType}
          </div>
        );
      }
    },
    {
      Header: 'CREATED',
      accessor: 'createdAt',
      Cell: (cell: CellProps<Tooltip>) => (
        <div>
          {moment(cell.row.original.createdAt).local().format('MM/DD/YYYY')}
        </div>
      )
    },
    {
      id: 'deactivate',
      Cell: (cell: CellProps<Tooltip>) => (
        <div
          className={'more-actions-btn more-actions-btn-unselected'}
          onClick={() => {
            deactivateTooltip(cell.row.original);
          }}
        >
          {CobuConstants.activeTooltips.deactivateSurvey}
        </div>
      )
    },
    {
      id: 'editTooltip',
      Cell: (cell: CellProps<Tooltip>) => (
        <div
          className={'more-actions-btn more-actions-btn-unselected'}
          onClick={() => {
            setTooltipToEdit(cell.row.original);
            setShowTooltipCreateModal(true);
          }}
        >
          {CobuConstants.activeTooltips.editToolTip}
        </div>
      )
    }
  ];

  const scheduledTooltipTableConfig = [
    {
      Header: 'BUILDING',
      accessor: 'building.name',
      Cell: (cell: CellProps<Tooltip>) => (
        <div style={{ whiteSpace: 'normal' }} className='default-text'>
          {cell.row.original.building?.name}
        </div>
      )
    },
    {
      Header: 'TITLE',
      accessor: 'title',
      Cell: (cell: CellProps<Tooltip>) => (
        <div style={{ whiteSpace: 'normal' }} className='default-text'>
          {cell.row.original.title}
        </div>
      )
    },
    {
      Header: CobuConstants.activeTooltips.scheduledStart,
      accessor: 'scheduledActiveDate',
      Cell: (cell: CellProps<Tooltip>) => (
        <div>
          {cell.row.original.scheduledActiveDate
            ? moment(cell.row.original.scheduledActiveDate)
                .local()
                .format('MM/DD/YYYY hh:mm A')
            : '—'}
        </div>
      )
    },
    {
      Header: CobuConstants.activeTooltips.scheduledEnd,
      accessor: 'scheduledDeactiveDate',
      Cell: (cell: CellProps<Tooltip>) => (
        <div>
          {cell.row.original.scheduledDeactiveDate
            ? moment(cell.row.original.scheduledDeactiveDate)
                .local()
                .format('MM/DD/YYYY hh:mm A')
            : '—'}
        </div>
      )
    },
    {
      Header: 'TYPE',
      accessor: 'linkType',
      Cell: (cell: CellProps<Tooltip>) => (
        <div style={{ whiteSpace: 'normal' }} className='default-text'>
          {cell.row.original.linkType}
        </div>
      )
    },
    {
      id: 'editScheduled',
      Cell: (cell: CellProps<Tooltip>) => (
        <div
          className={'more-actions-btn more-actions-btn-unselected'}
          onClick={() => {
            setTooltipToEdit(cell.row.original);
            setShowTooltipCreateModal(true);
          }}
        >
          {CobuConstants.activeTooltips.editToolTip}
        </div>
      )
    },
    {
      id: 'deleteScheduled',
      Cell: (cell: CellProps<Tooltip>) => (
        <div
          className={'more-actions-btn more-actions-btn-unselected'}
          onClick={() => {
            deactivateTooltip(cell.row.original);
          }}
        >
          {CobuConstants.activeTooltips.deleteTooltip}
        </div>
      )
    }
  ];

  const ALLOWED_CSV_COLUMNS = ['Building', 'Title', 'Call to Action', 'Link Location', 'Description', 'Link Type'];

  const validateCsvHeaders = (file: File): Promise<string | null> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const firstLine = (e.target?.result as string).split('\n')[0] || '';
        const headers = firstLine.split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
        const extra = headers.filter((h) => h && !ALLOWED_CSV_COLUMNS.includes(h));
        if (extra.length) {
          resolve(`CSV contains unexpected column(s): ${extra.join(', ')}. Allowed: ${ALLOWED_CSV_COLUMNS.join(', ')}.`);
        } else {
          resolve(null);
        }
      };
      reader.readAsText(file);
    });
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      return toast.error('Please select a CSV file first');
    }
    const headerError = await validateCsvHeaders(csvFile);
    if (headerError) {
      return toast.error(headerError);
    }
    setCsvUploading(true);
    try {
      const result = await bulkUploadTooltips(
        csvFile,
        csvScheduledActiveDate ? moment(csvScheduledActiveDate).toISOString() : undefined,
        csvScheduledDeactiveDate ? moment(csvScheduledDeactiveDate).toISOString() : undefined
      );
      setShowCsvUploadModal(false);
      setCsvFile(undefined);
      setCsvScheduledActiveDate('');
      setCsvScheduledDeactiveDate('');
      toast.success(`${result.created?.length ?? 0} tooltip(s) created successfully`);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to upload CSV');
    } finally {
      setCsvUploading(false);
    }
  };

  const createTooltipAction = () => {
    return (
      <button
        className='btn-primary btn-standard bold'
        onClick={() => {
          setTooltipToEdit(null);
          setShowTooltipCreateModal(!showTooltipCreateModal);
        }}
      >
        {CobuConstants.activeTooltips.createTooltip}
      </button>
    );
  };

  const uploadCsvAction = () => {
    if (!isSuperAdmin) return null;
    return (
      <button
        className='btn-primary btn-standard bold'
        onClick={() => {
          setCsvFile(undefined);
          setShowCsvUploadModal(true);
        }}
      >
        Upload CSV
      </button>
    );
  };

  if (loadingConfig.flag)
    return (
      <div className={styles.loadingContainer}>
        <Spinner />
        <h3 style={{ textAlign: 'center' }}>{loadingConfig.comment}</h3>
      </div>
    );

  const createTooltipModal = () => {
    return showTooltipCreateModal ? (
      <GenericModal
        showModal={showTooltipCreateModal}
        title={CobuConstants.activeTooltips.createTooltip}
        content={
          <CreateTooltipForm
            cancel={() => {
              setTooltipToEdit(null);
              setShowTooltipCreateModal(!showTooltipCreateModal);
            }}
            createTooltip={(tooltip) => {
              setShowTooltipCreateModal(!showTooltipCreateModal);
              createTooltip(tooltip);
            }}
            updateToolTip={(tooltip) => {
              setShowTooltipCreateModal(!showTooltipCreateModal);
              updateTooltip(tooltip);
              setTooltipToEdit(null);
            }}
            formConfig={getCreateTooltipFormConfig()}
            defaultData={tooltipToEdit}
          />
        }
        hideActionButton
        canceActionButton
        actionText={CobuConstants.residentManagement.createActionTitle}
        actionFunction={() => {}}
        onChange={() => {}}
      />
    ) : null;
  };

  const csvUploadModal = () => {
    if (!showCsvUploadModal) return null;
    return (
      <GenericModal
        showModal={showCsvUploadModal}
        title='Bulk Upload Tooltips via CSV'
        onChange={() => {
          setShowCsvUploadModal(false);
          setCsvFile(undefined);
          setCsvScheduledActiveDate('');
          setCsvScheduledDeactiveDate('');
        }}
        content={
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '8px 0' }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (e.dataTransfer.files?.[0]) setCsvFile(e.dataTransfer.files[0]);
            }}
          >
            <p style={{ textAlign: 'center', color: '#555' }}>
              CSV must include columns: <strong>Building</strong> (name), <strong>Title</strong>, Call to Action, Link Location, Description, Link Type
            </p>
            <div
              style={{ border: '2px dashed #ccc', borderRadius: '8px', padding: '32px 48px', cursor: 'pointer', textAlign: 'center' }}
              onClick={() => csvInputRef.current?.click()}
            >
              {csvFile ? csvFile.name : 'Drop your CSV here or click to browse'}
            </div>
            <input
              type='file'
              accept='.csv'
              ref={csvInputRef}
              style={{ display: 'none' }}
              onChange={(e) => {
                setCsvFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '13px' }}>
                  Schedule Start (optional)
                </label>
                <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#777' }}>
                  Leave blank to make tooltips live immediately
                </p>
                <input
                  type='datetime-local'
                  value={csvScheduledActiveDate}
                  onChange={(e) => setCsvScheduledActiveDate(e.target.value)}
                  style={{ width: '100%', padding: '6px 8px', border: '1px solid #ccc', borderRadius: '4px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '13px' }}>
                  Schedule End (optional)
                </label>
                <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#777' }}>
                  Leave blank for no automatic deactivation
                </p>
                <input
                  type='datetime-local'
                  value={csvScheduledDeactiveDate}
                  onChange={(e) => setCsvScheduledDeactiveDate(e.target.value)}
                  style={{ width: '100%', padding: '6px 8px', border: '1px solid #ccc', borderRadius: '4px' }}
                />
              </div>
            </div>
          </div>
        }
        actionText={csvUploading ? 'Uploading...' : 'Upload'}
        actionFunction={handleCsvUpload}
      />
    );
  };

  return (
    <div className='content-container'>
      <Breadcrumbs />
      <Spacer height={20} />
      <h1>{CobuConstants.activeTooltips.title}</h1>
      <Spacer height={20} />
      <Tabs.Container
        value={selectedTab}
        onChange={(val) => setSelectedTab(val as TabValue)}
      >
        <Tabs.Option value='active' selected={selectedTab === 'active'}>
          {CobuConstants.activeTooltips.activeTab}
        </Tabs.Option>
        <Tabs.Option value='scheduled' selected={selectedTab === 'scheduled'}>
          {CobuConstants.activeTooltips.scheduledTab}
        </Tabs.Option>
      </Tabs.Container>
      <Spacer height={20} />
      {selectedTab === 'active' ? (
        <CobuTableAdvanced
          columns={activeTooltipTableConfig}
          data={activeTooltips}
          noData={{
            label: CobuConstants.activeTooltips.noActiveSurveys,
            action: undefined,
            image: undefined
          }}
          actions={[createTooltipAction(), uploadCsvAction()].filter(Boolean) as React.ReactElement[]}
          actionsStyle={{ top: '130px' }}
        />
      ) : (
        <CobuTableAdvanced
          columns={scheduledTooltipTableConfig}
          data={scheduledTooltips}
          noData={{
            label: CobuConstants.activeTooltips.noScheduledTooltips,
            action: undefined,
            image: undefined
          }}
          actions={[createTooltipAction(), uploadCsvAction()].filter(Boolean) as React.ReactElement[]}
          actionsStyle={{ top: '130px' }}
        />
      )}
      {createTooltipModal()}
      {csvUploadModal()}
    </div>
  );
};

export default ActiveTooltips;
